// ARQUIVO ATUALIZADO
import React, { useMemo, useState } from 'react';
import useData from '../hooks/useData';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import { AddIcon, GroupIcon, ChevronDownIcon } from '../components/ui/Icons';
import DropdownMenu from '../components/ui/DropdownMenu';

const ProjectCard = ({ project, tasks, clients, onProjectClick }) => {
    const projectTasks = useMemo(() => tasks.filter(t => t.projectId === project.id), [tasks, project.id]);
    const completedTasks = useMemo(() => projectTasks.filter(t => t.status === 'Concluído'), [projectTasks]);
    
    const progress = projectTasks.length > 0 ? (completedTasks.length / projectTasks.length) * 100 : 0;
    const client = useMemo(() => clients.find(c => c.id === project.clientId), [clients, project.clientId]);

    return (
        <GlassCard 
            className="group relative flex flex-col justify-between cursor-pointer hover:bg-white/20 transition-all duration-200 hover:scale-[1.01]" 
            onClick={() => onProjectClick(project)}
        >
            <div>
                <div className="flex justify-between items-start">
                    <h4 className="font-bold text-white text-lg pr-8">{project.name}</h4>
                    <span className="text-sm text-gray-400 flex items-center gap-1.5 flex-shrink-0">
                        <GroupIcon size={14} />
                        {client?.name || 'Sem cliente'}
                    </span>
                </div>
                <p className="text-sm text-gray-300 mt-2 min-h-[40px]">{project.description || 'Nenhuma descrição adicionada.'}</p>
            </div>
            <div className="mt-4">
                <div className="flex justify-between items-center text-xs text-gray-300 mb-1">
                    <span>Progresso</span>
                    <span>{completedTasks.length} / {projectTasks.length}</span>
                </div>
                <div className="w-full bg-black/30 rounded-full h-2">
                    <div className="bg-indigo-500 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                </div>
            </div>
        </GlassCard>
    );
};

const ProjectsList = ({ onAddProject, onProjectClick }) => {
    const { projects, tasks, clients } = useData();
    // --- INÍCIO DA ALTERAÇÃO ---
    const [selectedClient, setSelectedClient] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');

    const getProjectStatus = (project, allTasks) => {
        const projectTasks = allTasks.filter(t => t.projectId === project.id && !t.isArchived);
        if (projectTasks.length === 0) return 'Não Iniciado';
        const completedTasks = projectTasks.filter(t => t.status === 'Concluído');
        if (completedTasks.length === projectTasks.length) return 'Concluído';
        return 'Em Andamento';
    };

    const clientOptions = useMemo(() => {
        const options = [{ label: 'Todos os Clientes', value: 'all' }];
        clients.forEach(c => options.push({ label: c.name, value: c.id }));
        return options;
    }, [clients]);

    const statusOptions = [
        { label: 'Todos os Status', value: 'all' },
        { label: 'Em Andamento', value: 'Em Andamento' },
        { label: 'Concluído', value: 'Concluído' },
        { label: 'Não Iniciado', value: 'Não Iniciado' },
    ];

    const filteredProjects = useMemo(() => {
        return projects
            .filter(project => selectedClient === 'all' || project.clientId === selectedClient)
            .filter(project => selectedStatus === 'all' || getProjectStatus(project, tasks) === selectedStatus);
    }, [projects, tasks, selectedClient, selectedStatus]);

    const getClientLabel = (value) => value === 'all' ? 'Todos os Clientes' : clients.find(c => c.id === value)?.name;
    const getStatusLabel = (value) => statusOptions.find(s => s.value === value)?.label;
    // --- FIM DA ALTERAÇÃO ---
    
    return (
        <div className="p-4 space-y-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white">Projetos</h2>
                {/* --- INÍCIO DA ALTERAÇÃO: Adicionados os dropdowns de filtro --- */}
                <div className="flex items-center gap-2">
                    <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-lg py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getStatusLabel(selectedStatus)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {statusOptions.map(option => (
                                <button key={option.value} onClick={() => setSelectedStatus(option.value)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600">
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>

                    <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-lg py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getClientLabel(selectedClient)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {clientOptions.map(option => (
                                <button key={option.value} onClick={() => setSelectedClient(option.value)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600">
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>

                    <GlassButton onClick={onAddProject} className="flex items-center space-x-2">
                        <AddIcon size={20} />
                        <span>Novo Projeto</span>
                    </GlassButton>
                </div>
                {/* --- FIM DA ALTERAÇÃO --- */}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map(project => (
                    <ProjectCard 
                        key={project.id}
                        project={project}
                        tasks={tasks}
                        clients={clients}
                        onProjectClick={onProjectClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default ProjectsList;